import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ExternalLink } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import type { MetricsTableRow } from './MetricsTable';
import type { HealthStatus } from '@/constants/analyticsThresholds';

export interface MetricFocusPanelProps {
  row: MetricsTableRow | null;
  className?: string;
}

const INTERPRETATIONS: Record<string, string> = {
  'Project finish vs target':
    'Compares the modeled project finish against the target or baseline. A positive delta means the project is forecast to finish late.',
  'Critical path duration':
    'Total duration of the longest path through the network. Changes indicate scope shifts or logic revisions on the driving path.',
  'Critical float remaining (min total float, days)':
    'Minimum total float across all activities. Negative values mean the schedule cannot meet the target finish without crashing or fast-tracking.',
  'Negative float activities':
    'Count of activities where total float is negative. These must be resolved — they indicate the schedule is over-committed.',
  'Critical or near-critical (float ≤5d)':
    'Activities with 5 days or less of total float. A high count means the schedule is fragile and has little buffer against disruption.',
  'Out-of-sequence':
    'Activities progressing out of planned logical sequence. Indicates actual execution is deviating from the planned network logic.',
  'High-float % (>44d)':
    'Proportion of activities with >44 working days of float. High values suggest the schedule may not be resource-loaded or logic is incomplete.',
};

const statusBadgeVariant: Record<HealthStatus, 'ok' | 'warning' | 'critical' | 'unknown'> = {
  ok: 'ok',
  warning: 'warning',
  critical: 'critical',
  unknown: 'unknown',
};

const STATUS_LABELS: Record<HealthStatus, string> = {
  ok: 'OK',
  warning: 'Warning',
  critical: 'Critical',
  unknown: 'Unknown',
};

/** Map internal source hints to user-facing labels. Returns null to omit the line. */
function humanSourceLabel(hint: 'api' | 'derived' | 'baseline' | null | undefined): string | null {
  // "api" is an internal label — omit rather than expose
  if (hint === 'derived') return 'Derived';
  if (hint === 'baseline') return 'Baseline';
  return null;
}

function getInterpretation(metricName: string): string | null {
  return INTERPRETATIONS[metricName] ?? null;
}

export const MetricFocusPanel: React.FC<MetricFocusPanelProps> = ({ row, className }) => {
  if (!row) {
    return (
      <div className={['rounded-md border border-border bg-card p-4 flex flex-col items-center justify-center min-h-[280px] text-center', className].filter(Boolean).join(' ')}>
        <div className="text-muted-foreground">
          <svg className="h-8 w-8 mx-auto mb-2 opacity-30" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 15l-2 5L9 9l11 4-5 2zm0 0l5 5M7.188 2.239l.777 2.897M5.136 7.965l-2.898-.777M13.95 4.05l-2.122 2.122m-5.657 5.656l-2.12 2.122" />
          </svg>
          <p className="text-xs font-medium">Select a metric</p>
          <p className="text-[11px] mt-1 text-muted-foreground/70">Click a row to see interpretation and drill-through.</p>
        </div>
      </div>
    );
  }

  const sourceLabel = humanSourceLabel(row.dataSourceHint);
  const interpretation = getInterpretation(row.metricName);

  return (
    <div className={['rounded-md border border-border bg-card overflow-hidden', className].filter(Boolean).join(' ')}>
      {/* Header */}
      <div className="border-b border-border bg-muted/30 px-3 py-2">
        <div className="flex items-center justify-between gap-2">
          <h3 className="text-xs font-semibold text-foreground truncate">{row.metricName}</h3>
          <Badge variant={statusBadgeVariant[row.status]} size="sm">{STATUS_LABELS[row.status]}</Badge>
        </div>
      </div>

      <div className="p-3 space-y-3">
        {/* Value strip */}
        <div className="flex items-baseline gap-4">
          <div>
            <span className="text-[10px] uppercase tracking-wider text-muted-foreground">Current</span>
            <p className="text-sm font-semibold tabular-nums text-foreground leading-tight">{row.currentValue}</p>
          </div>
          {row.delta != null && row.delta !== '' && row.delta !== '—' && (
            <div>
              <span className="text-[10px] uppercase tracking-wider text-muted-foreground">Delta</span>
              <p className="text-sm tabular-nums text-muted-foreground leading-tight">{row.delta}</p>
            </div>
          )}
          {sourceLabel != null && (
            <div>
              <span className="text-[10px] uppercase tracking-wider text-muted-foreground">Source</span>
              <p className="text-xs text-muted-foreground leading-tight">{sourceLabel}</p>
            </div>
          )}
        </div>

        {/* Interpretation — only when we have domain text */}
        {interpretation != null && (
          <div>
            <span className="text-[10px] uppercase tracking-wider text-muted-foreground block mb-0.5">Interpretation</span>
            <p className="text-xs text-foreground/80 leading-relaxed">{interpretation}</p>
          </div>
        )}

        {/* Evidence / Drivers — only shown when driver data exists */}
        {row.topContributor && (
          <div>
            <span className="text-[10px] uppercase tracking-wider text-muted-foreground block mb-0.5">Drivers</span>
            <Link
              to={row.topContributor.href}
              className="inline-flex items-center gap-1.5 text-xs font-medium text-primary hover:underline"
            >
              <span className="inline-block h-1.5 w-1.5 rounded-full bg-primary/60 shrink-0" />
              {row.topContributor.label}
              <ExternalLink className="h-3 w-3 shrink-0 opacity-60" />
            </Link>
          </div>
        )}

        {/* Next actions */}
        <div className="pt-1 border-t border-border/50">
          <span className="text-[10px] uppercase tracking-wider text-muted-foreground block mb-1.5">Actions</span>
          <div className="flex flex-col gap-1.5">
            <Button variant="default" size="sm" asChild className="h-7 text-xs justify-start">
              <Link to={row.viewHref}>
                <ArrowRight className="h-3 w-3 mr-1.5" />
                {row.viewLabel}
              </Link>
            </Button>
            {row.topContributor && row.topContributor.href !== row.viewHref && (
              <Button variant="outline" size="sm" asChild className="h-7 text-xs justify-start">
                <Link to={row.topContributor.href}>
                  <ExternalLink className="h-3 w-3 mr-1.5" />
                  Inspect: {row.topContributor.label.split(': ')[1] ?? row.topContributor.label}
                </Link>
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
